export interface IObservable<T> {
	/**
	 * Подписка на изменение значения. При подписке будет вызван callback с текущим значением
	 */
	subscribe(callback: (value: T) => void): ISubscription;

	/**
	 * Возвращает текущее значение
	 */
	getValue(): T;

	/**
	 * Возвращает начальное значение
	 */
	getInitialValue(): T;
}

export interface ISubject<T> {
	/**
	 * Устанавливает новое значение и уведомляет всех подписчиков
	 */
	next(value: T): void;
}

export interface ISubscription {
	/**
	 * Отписка от изменения значения
	 */
	unsubscribe(): void;
}

export interface ICallback<T> {
	(value: T): void;
}

export class Subject<T> implements ISubject<T>, IObservable<T> {
	private subscriptions: Subscription<T>[] = [];

	private value: T;
	private initialValue: T;

	constructor(initialValue: T) {
		this.value = initialValue;
		this.initialValue = initialValue;
	}

	public next(value: T): void {
		this.value = value;
		this.subscriptions.forEach(({ callback }) => callback(value));
	}

	public getValue(): T {
		return this.value;
	}

	public getInitialValue(): T {
		return this.initialValue;
	}

	public getCount(): number {
		return this.subscriptions.length;
	}

	public subscribe(callback: (value: T) => void): ISubscription {
		let subscription = this.subscriptions.find(
			(sub) => sub.callback === callback,
		);
		if (subscription) {
			return subscription;
		}

		subscription = new Subscription(this, callback);
		this.subscriptions.push(subscription);
		callback(this.value);

		return subscription;
	}

	public unsubscribe(callback: (value: T) => void): void {
		const index = this.subscriptions.findIndex(
			(sub) => sub.callback === callback,
		);
		if (index === -1) {
			return;
		}

		this.subscriptions.splice(index, 1);
	}
}

class Subscription<T> implements ISubscription {
	constructor(
		private readonly subject: Subject<T>,
		public readonly callback: (value: T) => void,
	) {}

	public unsubscribe(): void {
		this.subject.unsubscribe(this.callback);
	}
}
